import { useEffect, useState } from "react";
import { getCollectionRequest } from "services/apiRequests";
import { usePlatform } from "components/utils/PlatformContext";
import ImageSkeleton from "utils/skeletons/ImageSkeleton";

interface MyBadgesProps {}

const MyBadges: React.FC<MyBadgesProps> = ({}) => {
  // States definition
  const [badges, setBadges] = useState<string[]>([]);

  // Get the platform context
  const { badgesWatermark } = usePlatform();

  // Parameter definition
  const maxAmountOfBadges = 4;

  /**
   * Get the data every time it's rendered
   */
  useEffect(() => {
    getData();
  }, []);

  /**
   * Function used to request the progress values
   */
  const getData = async () => {
    // Get the knowledge points
    const response = await getCollectionRequest("/api/users/my-badges");

    // Check if the response is successful
    if (!response.successful) {
      return;
    }

    // Setting the id of the badges
    setBadges(response.content);

    return response;
  };

  return (
    <div className="self-stretch h-[390px] min-h-[390px] bg-neutral-25 rounded-3xl flex-col justify-between items-start flex overflow-hidden relative">
      {badgesWatermark && (
        <img
          className="absolute w-auto h-full right-0 select-none"
          src={badgesWatermark}
          alt="My badges watermark"
        />
      )}
      <div className="self-stretch p-6 items-start inline-flex select-none">
        {badges
          .slice(-maxAmountOfBadges)
          .map((badge: string, index: number) => (
            <ImageSkeleton
              key={index}
              src={badge}
              alt={`Badge ${index + 1}`}
              style={{
                right: index * 28,
              }}
              className="w-20 h-20 relative overflow-hidden"
            />
          ))}
        {badges.length > maxAmountOfBadges && (
          <div
            className="w-20 h-20 min-w-20 relative z-20 inline-flex items-center justify-center"
            style={{
              right: maxAmountOfBadges * 28,
            }}
          >
            <div className="w-14 h-14 absolute bg-neutral-25/80 shadow border-4 border-[#d18732]/40 rounded-md justify-center items-center inline-flex rotate-45">
              <div className="text-[#955309]/80 text-[24px] font-semibold font-sans leading-10 -rotate-45">
                {badges.length - maxAmountOfBadges}+
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="self-stretch p-6 justify-between items-end inline-flex z-10">
        <div className="grow shrink basis-0 flex-col justify-start items-start gap-4 inline-flex">
          <div className="self-stretch relative">
            <div className="text-neutral-700 text-[33px] font-semibold font-sans leading-10 select-none">
              {badges.length || 0}
            </div>
          </div>
          <div className="self-stretch h-fit flex-col justify-start items-start gap-2 flex">
            <div className="self-stretch text-neutral-700 text-lg font-normal font-sans leading-snug select-none">
              My Badges
            </div>
            <div className="self-stretch text-neutral-500 text-xs font-normal font-sans leading-[14px] select-none">
              You have earned {badges.length}{" "}
              {badges.length === 1 ? "badge" : "badges"} so far
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyBadges;
